import { useEffect, useState, type ReactNode } from "react";
import { MobileSheet } from "./MobileSheet.tsx";
import i18n from "../util/i18n.ts";
import {
	MobileActionableEmptyState,
	MobileContentHero,
	MobileEntityRow,
	MobileExpertDisclosure,
	MobileStatRow,
} from "../mobile/mobilePrimitives.tsx";
import { toWorker } from "../util/toWorker.ts";
import {
	getAchievementsSummary,
	type AchievementRow,
	type UnlockedAchievement,
} from "../views/achievementsSummary.ts";

// M3-3: the standalone achievements page (view "achievements", global route
// /achievements) was retired into this bottom-sheet, opened in place from the
// league-history hub. It shows the same GM trophy-case content the page did —
// completion status, unlocked list, locked/help expert depth, and the empty
// state — but as an overlay that never touches the navigation back stack (see
// MOBILE_SHEET_CONTRACT_V1 in MobileSheet.tsx). The achievement roster is
// account-level (idb.meta), so it is fetched ad hoc on open via the
// `getAchievementsSheet` worker RPC (the M3 data pattern), not the page-view
// pipeline.

const translateAchievementName = (achievement: AchievementRow) =>
	i18n.t(`achievement.name.${achievement.slug}`, {
		ns: "ui",
		defaultValue: achievement.name,
	});

const translateAchievementDesc = (achievement: AchievementRow) =>
	i18n.t(`achievement.desc.${achievement.slug}`, {
		ns: "ui",
		defaultValue: achievement.desc,
	});

const translateAchievementCategory = (achievement: AchievementRow) =>
	i18n.t(`achievement.category.${achievement.category}`, {
		ns: "ui",
		defaultValue: achievement.category,
	});

const AchievementRowItem = ({
	achievement,
	locked,
	trailing,
}: {
	achievement: AchievementRow;
	locked?: boolean;
	trailing?: ReactNode;
}) => (
	<MobileEntityRow
		data-achievement-slug={achievement.slug}
		data-achievement-state={locked ? "locked" : "unlocked"}
		detail={translateAchievementDesc(achievement)}
		highlighted={!locked}
		meta={translateAchievementCategory(achievement)}
		title={translateAchievementName(achievement)}
		trailing={trailing}
	/>
);

export const AchievementsSheet = ({
	onClose,
	open,
}: {
	onClose: () => void;
	open: boolean;
}) => {
	const [achievements, setAchievements] = useState<AchievementRow[] | undefined>(
		undefined,
	);

	useEffect(() => {
		if (!open) {
			return;
		}

		let cancelled = false;
		setAchievements(undefined);
		(async () => {
			const data = await toWorker("main", "getAchievementsSheet", undefined);
			if (!cancelled) {
				setAchievements(data.achievements);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [open]);

	const timesEarned = (achievement: UnlockedAchievement) =>
		i18n.t("achievements.timesEarned", {
			ns: "ui",
			count: achievement.count,
			defaultValue: "×{{count}}",
		});

	const renderBody = () => {
		if (achievements === undefined) {
			return (
				<div
					className="d-flex justify-content-center py-4"
					data-achievements-sheet-loading=""
				>
					<div className="spinner-border text-secondary" role="status">
						<span className="visually-hidden">
							{i18n.t("common.loading", { ns: "ui", defaultValue: "Loading…" })}
						</span>
					</div>
				</div>
			);
		}

		const summary = getAchievementsSummary(achievements);
		const hasUnlocked = summary.unlocked.length > 0;
		const lockedList =
			summary.locked.length > 0 ? (
				<MobileExpertDisclosure
					className="mt-3"
					count={i18n.t("achievements.lockedCount", {
						ns: "ui",
						count: summary.locked.length,
						defaultValue: "{{count}} locked",
					})}
					data-achievements-locked-list=""
					expertHostId="achievements-locked-list"
					summary={i18n.t("achievements.lockedSummary", {
						ns: "ui",
						defaultValue: "Locked achievements",
					})}
				>
					<div role="list">
						{summary.locked.map((achievement) => (
							<AchievementRowItem
								achievement={achievement}
								key={achievement.slug}
								locked
							/>
						))}
					</div>
				</MobileExpertDisclosure>
			) : null;

		if (!hasUnlocked) {
			return (
				<div data-achievements-view="" data-achievements-empty="">
					{/* G4-1: the achievements sheet is a "tool" container whose only exit
					    is the header X. Drop the empty-state "Close" button that duplicated
					    that X (UX review 意见1) — the empty state is title + reason + impact. */}
					<MobileActionableEmptyState
						impact={i18n.t("achievements.emptyImpact", {
							ns: "ui",
							defaultValue:
								"Win games and titles at Normal or higher difficulty to start earning them.",
						})}
						reason="not-started"
						reasonText={i18n.t("achievements.emptyReason", {
							ns: "ui",
							defaultValue: "You haven't unlocked any achievements yet.",
						})}
						title={i18n.t("achievements.emptyTitle", {
							ns: "ui",
							defaultValue: "No achievements yet",
						})}
					/>
					{lockedList}
				</div>
			);
		}

		return (
			<div data-achievements-view="">
				<MobileContentHero
					data-achievements-content-first=""
					explainer={i18n.t("achievements.mobileExplainer", {
						ns: "ui",
						defaultValue:
							"Earn achievements by winning at Normal or higher difficulty.",
					})}
				>
					<MobileStatRow
						data-achievements-completion=""
						detail={i18n.t("achievements.ignoringMeta", {
							ns: "ui",
							defaultValue: "(Ignoring the Meta achievements)",
						})}
						label={i18n.t("achievements.completionStatus", {
							ns: "ui",
							defaultValue: "Completion Status",
						})}
						tone="positive"
						value={`${summary.unlockedCountable} / ${summary.countable}`}
					/>
					<div data-achievements-unlocked-list="" role="list">
						{summary.unlocked.map((achievement) => (
							<AchievementRowItem
								achievement={achievement}
								key={achievement.slug}
								trailing={
									<span
										className="badge text-bg-success"
										data-achievements-earned-count=""
									>
										{timesEarned(achievement)}
									</span>
								}
							/>
						))}
					</div>
				</MobileContentHero>

				{lockedList}

			</div>
		);
	};

	return (
		<MobileSheet
			onClose={onClose}
			open={open}
			title={i18n.t("achievements.title", { ns: "ui", defaultValue: "Achievements" })}
		>
			{renderBody()}
		</MobileSheet>
	);
};
